/**
 * The Context tab body — task 5 (Tossker repaint), README §5 "Context
 * pane": the TICKET card, then (voice only) THIS CALL, then WHO THIS IS,
 * then THIS THREAD, as key/value rows. `detail` is the `ThreadDetail` the
 * parent (ContextPane) fetched from `ops.thread()` — this component only
 * formats it, never fetches or invents anything itself.
 *
 * WHO THIS IS / THIS THREAD are the renamed IDENTITY / THREAD blocks from
 * the pre-repaint pane — same fields, same testids (`identity-row-*` /
 * `thread-row-*`), only the eyebrow text and the two additions README §5
 * calls for change: a "Not her? Change" button on WHO THIS IS (wired to the
 * same Match modal `ThreadHeader`'s tier badge already reopens) and a
 * waiting-time progress bar on THIS THREAD.
 */
import { useEffect, useState } from 'react'
import type { ThreadDetail } from '@communicator/shared'
import { Icon } from '../ui'
import {
  CALL_DIRECTION_LABEL,
  callTalkingLabel,
  channelRowLabel,
  contextLivenessLabel,
  contextSlaLabel,
  hasNoRecording,
  lastSyncedLabel,
  NO_RECORDING_TEXT,
  voiceSourceLabel,
  waitProgressFraction,
  waitProgressLabel,
} from './format'
// Visual findings item 3: the Confidence row's tier pill reuses the thread
// header's own badge — icon, title-case text, and status/tint colours —
// rather than this pane's old, now-removed ALL-CAPS `TIER_LABEL` mapping.
// `../thread/format.ts` owns this vocabulary; see its own comment and the
// removed constants' replacement comment in `./format.ts`.
import { TIER_BADGE_ICON, TIER_BADGE_TEXT, tierBadgeStyle } from '../thread/format'
import { KvRow } from './KvRow'
import { TicketSection } from './TicketSection'
import { selectTimeZone, useStore } from '../../store'

export type TranscriptTurnSpeaker = 'tech' | 'caller'

export interface TranscriptTurn {
  speaker: TranscriptTurnSpeaker
  text: string
}

const TURN_PREFIXES: { prefix: string; speaker: TranscriptTurnSpeaker }[] = [
  { prefix: 'Tech:', speaker: 'tech' },
  { prefix: 'Caller:', speaker: 'caller' },
]

/**
 * Task 3 (2026-09-12, speaker-labeled transcripts). `transcriptLabeled`
 * (`CallFacts` in `@communicator/shared`) is plain text: one line per turn,
 * "Tech: ..." / "Caller: ...". The labeling model wraps a long turn onto its
 * own following line WITHOUT repeating the prefix — so a line matching
 * neither prefix is a CONTINUATION of whichever turn came immediately
 * before it, not a turn of its own. Getting this backwards (starting a new,
 * unlabeled turn per line) silently drops the wrapped remainder onto the
 * wrong row — see task-3-report.md for how this was bite-proofed by
 * temporarily doing exactly that and watching the continuation-line tests
 * (test/context.test.tsx, "parseTurns") fail.
 *
 * A line before any recognized prefix has no turn to attach to and is
 * dropped — defensive only; the labeler always opens with a speaker line.
 */
export function parseTurns(labeled: string): TranscriptTurn[] {
  const turns: TranscriptTurn[] = []
  for (const line of labeled.split('\n')) {
    const match = TURN_PREFIXES.find(({ prefix }) => line.startsWith(prefix))
    if (match) {
      turns.push({ speaker: match.speaker, text: line.slice(match.prefix.length).trimStart() })
    } else if (turns.length > 0) {
      turns[turns.length - 1].text += `\n${line}`
    }
  }
  return turns
}

const SPEAKER_LABEL: Record<TranscriptTurnSpeaker, string> = { tech: 'Tech', caller: 'Caller' }
// Tech uses the same staff-accent token as the tech Avatar role
// (../ui/Avatar.tsx's `text-role-tech`); Caller uses the ordinary
// mid-emphasis text token — this is a transcript row, not a role badge.
const SPEAKER_CLASS: Record<TranscriptTurnSpeaker, string> = { tech: 'text-role-tech', caller: 'text-mid' }

export interface ContextTabProps {
  detail: ThreadDetail | null
  haloUp: boolean
  /**
   * The contact's last-reported client app version (design
   * 2026-08-24-client-version-visibility, `../context/clientPresence.ts`),
   * `null` when unknown or not applicable (non-chat threads, an unreachable
   * relay, no version ever reported). Fetched by the parent — this component
   * only formats what it is given, same discipline as `detail` itself.
   */
  clientVersion: string | null
  onSelectSibling: (id: string) => void
  onForceSync: () => void
  onChangeTicket: () => void
  onAttachTicket: () => void
  /** Reopens identity matching — README §5's "'Not her? Change' button wired to the existing Match modal (`onChangeIdentity`)." */
  onChangeIdentity: () => void
}

export function ContextTab({
  detail,
  haloUp,
  clientVersion,
  onSelectSibling,
  onForceSync,
  onChangeTicket,
  onAttachTicket,
  onChangeIdentity,
}: ContextTabProps) {
  const timeZone = useStore((s) => selectTimeZone(s.cfg))
  const [showRawTranscript, setShowRawTranscript] = useState(false)
  // ContextTab is not remounted per thread (ContextPane renders it without a
  // `key` — same reason CallSummary.tsx's own note-draft comment gives), so
  // without this the toggle would carry a "view raw" choice from one call
  // over onto the next thread's transcript.
  useEffect(() => {
    setShowRawTranscript(false)
  }, [detail?.id])
  if (!detail) {
    return (
      <div className="text-[11px] leading-[1.5] text-lo" data-testid="context-tab-empty">
        Select a thread to see its context.
      </div>
    )
  }

  const callState = detail.call?.state ?? null

  return (
    <div className="flex flex-col gap-4" data-testid="context-tab">
      <TicketSection
        detail={detail}
        haloUp={haloUp}
        onSelectSibling={onSelectSibling}
        onForceSync={onForceSync}
        onChangeTicket={onChangeTicket}
        onAttachTicket={onAttachTicket}
      />

      {detail.channel === 'voice' && detail.call && (
        <div>
          <div className="mb-[7px] text-[10px] font-semibold tracking-[.08em] text-lo">THIS CALL</div>
          <div className="flex flex-col gap-[5px]">
            <KvRow testId="call-row-direction" label="Direction" value={CALL_DIRECTION_LABEL[detail.call.direction]} valueClassName="font-mono text-hi" />
            <KvRow
              testId="call-row-talking"
              label="Talking"
              value={callTalkingLabel(detail.call.state, detail.call.durationS)}
              valueClassName="font-mono text-hi"
            />
            <KvRow testId="call-row-extension" label="Extension" value={detail.call.ext ?? '—'} valueClassName="font-mono text-hi" />
            <KvRow testId="call-row-source" label="Source" value={voiceSourceLabel(detail.channel)} valueClassName="font-mono text-hi" />
            {hasNoRecording(detail.call) && (
              <div data-testid="call-no-recording" className="mt-0.5 text-[10.5px] leading-[1.5] text-lo">
                {NO_RECORDING_TEXT}
              </div>
            )}
            {detail.call.recordingUrl && (
              <div className="mt-0.5 text-[10.5px] leading-[1.5]">
                <a
                  data-testid="call-recording-link"
                  href={detail.call.recordingUrl}
                  target="_blank"
                  rel="noopener"
                  className="font-semibold text-cc-blue-text"
                >
                  Recording
                </a>
              </div>
            )}
            {detail.call.callTranscriptState === 'ready' && detail.call.callTranscript ? (
              <div className="mt-1.5">
                <div className="mb-[3px] flex items-center justify-between gap-2">
                  <div className="text-[10px] font-semibold tracking-[.08em] text-lo">Call transcript</div>
                  {detail.call.transcriptLabeled && (
                    <button
                      type="button"
                      data-testid="transcript-view-raw"
                      onClick={() => setShowRawTranscript((v) => !v)}
                      className="text-[10px] font-semibold text-lo underline hover:text-hi focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      {showRawTranscript ? 'view turns' : 'view raw'}
                    </button>
                  )}
                </div>
                {detail.call.transcriptLabeled && !showRawTranscript ? (
                  <div
                    data-testid="call-transcript"
                    className="max-h-[240px] overflow-y-auto text-[10.5px] leading-[1.5] text-hi"
                  >
                    {parseTurns(detail.call.transcriptLabeled).map((turn, i) => (
                      <div key={i} className="mb-1.5 last:mb-0">
                        <span className={`mr-1 whitespace-nowrap text-[10px] font-bold ${SPEAKER_CLASS[turn.speaker]}`}>
                          {/* Real people over role words (Andrew, 2026-09-12):
                            * the pane already knows both — the handler through
                            * the PBX directory, the caller through the thread's
                            * own display name (which falls back to the bare
                            * handle, still better than "Caller"). The role
                            * words survive only as the last-resort fallback. */}
                          {turn.speaker === 'tech'
                            ? detail.call?.handlerName ?? SPEAKER_LABEL.tech
                            : detail.who || SPEAKER_LABEL.caller}
                        </span>
                        <span className="whitespace-pre-wrap break-words">{turn.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    data-testid="call-transcript"
                    className="max-h-[240px] overflow-y-auto whitespace-pre-wrap break-words text-[10.5px] leading-[1.5] text-hi"
                  >
                    {detail.call.callTranscript}
                  </div>
                )}
              </div>
            ) : detail.call.callTranscriptState === 'unavailable' ? (
              <div data-testid="call-transcript-unavailable" className="mt-0.5 text-[10.5px] leading-[1.5] text-lo">
                No transcript — this call was not answered.
              </div>
            ) : (
              <div data-testid="call-transcript-pending" className="mt-0.5 text-[10.5px] leading-[1.5] text-lo">
                Transcript not in yet.
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <div className="mb-[7px] flex items-center justify-between gap-2">
          <div className="text-[10px] font-semibold tracking-[.08em] text-lo">WHO THIS IS</div>
          <button
            type="button"
            data-testid="who-this-is-change"
            onClick={onChangeIdentity}
            title="Not her? Reopen identity matching"
            className="text-[10.5px] font-semibold text-cc-blue-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Not her? Change
          </button>
        </div>
        <div className="flex flex-col gap-[5px]">
          <KvRow
            testId="identity-row-confidence"
            label="Confidence"
            value={
              <span
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-[20px] px-2 py-[3px] text-[11px] font-semibold"
                style={tierBadgeStyle(detail.tier)}
              >
                <Icon name={TIER_BADGE_ICON[detail.tier]} size={11} />
                {TIER_BADGE_TEXT[detail.tier]}
              </span>
            }
          />
          <KvRow
            testId="identity-row-client"
            label="Client"
            value={detail.client ?? 'unmapped'}
            valueClassName={detail.client ? 'text-hi' : 'text-warn'}
          />
          {/* ThreadDetail carries no contact job-title field in Phase 1 (packages/shared/src/types.ts) —
              the prototype's demo "Practice Manager" has no real source, so this renders an honest
              blank rather than a guess (resolution #1's "invent no detail whatsoever", applied here too). */}
          <KvRow testId="identity-row-role" label="Role" value="—" />
          <KvRow testId="identity-row-endpoint" label="Endpoint" value={detail.endpoint ?? '—'} />
        </div>
      </div>

      <div>
        <div className="mb-[7px] text-[10px] font-semibold tracking-[.08em] text-lo">THIS THREAD</div>
        <div className="flex flex-col gap-[7px]">
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-lo">Waiting</span>
              <span data-testid="thread-row-wait-progress" className="font-mono text-[11px] text-hi">
                {waitProgressLabel(detail.waitS, detail.slaTargetS)}
              </span>
            </div>
            <div className="mt-1 h-[5px] w-full overflow-hidden rounded-full bg-sunken">
              <div
                data-testid="thread-wait-progress-bar"
                className="h-full rounded-full"
                style={{ width: `${waitProgressFraction(detail.waitS, detail.slaTargetS) * 100}%`, background: 'var(--brand-primary)' }}
              />
            </div>
          </div>
          <KvRow
            testId="thread-row-channel"
            label="Channel"
            value={channelRowLabel(detail.channel, { version: clientVersion, targetId: detail.targetId })}
          />
          <KvRow
            testId="thread-row-liveness"
            label="Liveness"
            value={contextLivenessLabel(detail.channel, detail.liveness, callState)}
          />
          <KvRow
            testId="thread-row-sla"
            label="SLA target"
            value={contextSlaLabel(detail.channel, callState, detail.slaTargetS)}
          />
          <KvRow
            testId="thread-row-voice-source"
            label="Voice source"
            value={voiceSourceLabel(detail.channel)}
            valueClassName={detail.channel === 'voice' ? 'text-hi' : 'text-lo'}
          />
          <KvRow
            testId="thread-row-last-synced"
            label="Last synced"
            value={lastSyncedLabel(detail.ticket !== null, detail.lastSyncedAt, timeZone)}
          />
          <KvRow testId="thread-row-owner" label="Owner" value={detail.owner ?? 'unclaimed'} />
        </div>
      </div>
    </div>
  )
}
